import React, { useState, useCallback, useRef, useEffect } from 'react';
import clsx from 'clsx';
import {
  Box,
  makeStyles,
  Typography,
  IconButton,
  Slide,
  Hidden
} from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';
import AppButton from 'src/components/AppButton';
import AppModal from 'src/components/AppModal';
import SigninBg from 'src/assets/images/signin-bg.svg';
import CrossIcon from 'src/assets/images/cross-lg.svg';
import SigninForm from './SigninForm';
import ProfileForm from './ProfileForm';
import VerifyPage from 'src/components/VerifyPage';
import useAuth from 'src/hooks/useAuth';
import profileService from 'src/services/ProfileService';
import { Mixpanel } from 'src/lib/mixpanel';

const useStyles = makeStyles((theme: Theme) => ({
  root: {},
  btn: {
    textTransform: 'capitalize',
    padding: `${theme.spacing(0.75)}px ${theme.spacing(3)}px`
  },
  modalContent: {
    maxHeight: 640
  },
  left: {
    backgroundColor: theme.palette2.background.signin,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: 'min-content',
    padding: `${theme.spacing(10.25)}px ${theme.spacing(0.5)}px ${theme.spacing(
      11.25
    )}px`,
    gap: `${theme.spacing(8)}px`,
    '& p': {
      fontSize: 24,
      fontWeight: 500,
      lineHeight: '40px',
      padding: `0px ${theme.spacing(2.625)}px`,
      textAlign: 'center'
    },
    '& img': {
      width: 303,
      height: 283
    }
  },
  right: {
    width: '100%'
  },
  header: {
    '& .title': {
      fontSize: 20,
      fontWeight: 700,
      [theme.breakpoints.down('sm')]: {
        fontSize: 16
      }
    },
    '& .close': {
      padding: 0
    }
  },
  label: {
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 500
  }
}));

const Signin: React.FC = () => {
  const classes = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState(1);
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>();
  const [invalidOtp, setInvalidOtp] = useState(false);
  const authCtx = useAuth();

  const handleOpen = () => {
    authCtx.toggleSignInPopup(true);
  };
  const handleClose = () => {
    authCtx.toggleSignInPopup(false);
    authCtx.setNewUser(false);
  };
  const handleSignin = (data: any) => {
    authCtx.sendOtp(`${data.code}${data.phone_number}`);
    setPhoneNumber(data.phone_number);
    setStep(2);
  };
  const handleOtpSubmit = (otp: string) => {
    const phoneNumber = authCtx.user?.phone_number;
    if (!phoneNumber) return;
    authCtx.verifyOtp(phoneNumber, otp);
  };
  const handleResend = (type: string) => {
    const phoneNumber = authCtx.user?.phone_number;
    if (!phoneNumber) return;
    authCtx.resendOtp(phoneNumber, type);
  };
  const goToFirstStep = () => {
    setStep(1);
  };
  const resetOtpError = useCallback(() => {
    setInvalidOtp(false);
  }, [setInvalidOtp]);

  const handleProfileForm = async (data: any) => {
    const id = authCtx.user?.id;
    if (!id) return;
    await profileService.updateProfile(id, {
      user: {
        full_name: data.full_name,
        gender: data.gender,
        email: data.email
      }
    } as any);
    authCtx.setNewUser(false);
    authCtx.toggleSignInPopup(false);
  };

  // const renderCaptcha = () => {
  //   console.log('========', process.env.REACT_APP_AWS_WAP_CAPTCHA_APIKEY);
  //   AwsWafCaptcha.renderCaptcha(document.getElementById('signUp'), {
  //     apiKey: process.env.REACT_APP_AWS_WAP_CAPTCHA_APIKEY,
  //     onSuccess: () => {},
  //     onError: () => {},
  //     dynamicWidth: true,
  //     skipTitle: true
  //   });
  // }

  useEffect(() => {
    // renderCaptcha();
    return () => {
      setStep(1);
    };
  }, [authCtx.isSigninPopupOpen]);

  useEffect(() => {
    const { isAuthenticated, user } = authCtx;
    if (user === null) return;
    if (!isAuthenticated) {
      if (user.confirmed === false) {
        setInvalidOtp(true);
      }
      return;
    }
    if (user.new_record) {
      // new record
      const eventName = 'First App Open';
      Mixpanel.track(eventName, {
        eventName,
        userProperties: {
          phone_number: user.phone_number
        }
      });
      setStep(3);
    } else {
      authCtx.toggleSignInPopup(false);
    }
  }, [authCtx]);

  return (
    <Box className={classes.root}>
      <AppButton className={clsx('primary', classes.btn)} onClick={handleOpen}>
        Sign in
      </AppButton>
      <AppModal
        open={authCtx.isSigninPopupOpen}
        onClose={handleClose}
        maxWidth="md"
        className={classes.modalContent}
        id='signUp'
      >
        <Box display="flex">
          <Hidden smDown>
            <Box className={classes.left}>
              <Typography>
                Sign in/ Sign up to unlock personalization features!
              </Typography>
              <img src={SigninBg} alt="bg" />
            </Box>
          </Hidden>

          <Box className={classes.right}>
            <Box
              className={classes.header}
              py={2.25}
              pl={4}
              pr={2}
              textAlign="center"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box>
                <Typography className="title">
                  {step === 3
                    ? 'Add profile details'
                    : 'Sign in/Sign up to Cookd'}
                </Typography>
              </Box>
              <Box>
                <IconButton className="close" onClick={handleClose}>
                  <img src={CrossIcon} alt="cross" />
                </IconButton>
              </Box>
            </Box>

            <Box {...{ ref: containerRef } as any}>
              {step === 1 && (
                <Slide
                  direction="left"
                  in={step === 1}
                >
                  <Box>
                    <SigninForm onSubmit={handleSignin} />
                  </Box>
                </Slide>
              )}
              {step === 2 && phoneNumber && (
                <Slide
                  direction="left"
                  in={step === 2 && !!phoneNumber}
                >
                  <Box>
                    <VerifyPage
                      sentTo={phoneNumber}
                      onVerify={handleOtpSubmit}
                      onChange={goToFirstStep}
                      onResend={handleResend}
                      resetOtpError={resetOtpError}
                      invalid={invalidOtp}
                    />
                  </Box>
                </Slide>
              )}
              {step === 3 && (
                <Slide
                  direction="left"
                  in={step === 3}
                >
                  <Box>
                    <ProfileForm onSubmit={handleProfileForm} />
                  </Box>
                </Slide>
              )}
            </Box>
          </Box>
        </Box>
      </AppModal>
    </Box>
  );
};

export default Signin;
